// @tennis-matches/services/governance — Vertrauens-Heuristiken für Vereins-Discover & DQ

/** @typedef {"untrusted-host"|"non-club-name"|"name-mismatch"|"email-domain-mismatch"|"ok"|"no-website"} ClubSourceTrustReason */

/** Verzeichnisse, Medien, Portale — keine Vereins-Websites */
export const UNTRUSTED_CLUB_WEBSITE_HOSTS = new Set([
  "facebook.com",
  "instagram.com",
  "wikipedia.org",
  "linkedin.com",
  "youtube.com",
  "google.com",
  "twitter.com",
  "x.com",
  "dtb.de",
  "ltbv.de",
  "tennis.de",
  "tennisportal.de",
  "tennisnavigator.de",
  "vereinsmeier.de",
  "sportswanted.de",
  "meinestadt.de",
  "11880.com",
  "11880.de",
  "gelbeseiten.de",
  "yelp.de",
  "main-echo.de",
  "mainpost.de",
  "deutschlandfunk.de",
  "deutschlandfunkkultur.de",
  "dradio.de",
  "br.de",
  "mdr.de",
  "wdr.de",
  "ndr.de",
  "swr.de",
  "rbb-online.de",
  "werkenntdenbesten.de",
  "branchenbuch.de",
  "cylex.de",
  "stadtbranchenbuch.com",
  "golocal.de",
  "dastelefonbuch.de",
  "northdata.de",
  "firmenwissen.de",
  "tvpro-online.de",
  "pnp.de",
  "kn-online.de",
  "merkur.de",
  "tz.de",
  "sueddeutsche.de",
  "faz.net",
  "zeit.de",
  "spiegel.de",
  "focus.de",
  "bild.de",
  "tagesspiegel.de",
  "n-tv.de",
  "sport1.de",
  "sport.de",
  "padelfinder.de",
  "niederbayern.tv.de",
]);

/** @type {readonly RegExp[]} */
export const NON_CLUB_ENTITY_NAME_PATTERNS = Object.freeze([
  /\bwerkenntden/i,
  /\bturnierportal\b/i,
  /\btvpro\b/i,
  /\b\d+\s+best(en|e)\b/i,
  /\bbest(en|e)\s+\d+\b/i,
  /\bdie\s+(aktuell\s+)?\d+\s+best/i,
  /\b(top|beste)\s+\d+\s+tennis/i,
  /\b\d+\s+(beste|top)\s+tennisvereine?\b/i,
  /\btennisvereine?\s+in\s+[a-zäöüß]/i,
  /\b(ranking|vergleich|verzeichnis|branchenbuch)\b/i,
  /\bportal\b.*\b(turnier|tennis)\b/i,
  /\b(turnier|tennis).*\bportal\b/i,
  /\bturnierkalender\b/i,
  /\bbesiegt\b/i,
  /\brettet\b/i,
  /\bspielen leicht gemacht\b/i,
  /-\s*pnp\.de/i,
  /padelfinder\.de/i,
  /\btennisschule\b/i,
  /\btennis-?schule\b/i,
  /\btennisacademy\b/i,
  /\btennis-?academy\b/i,
  /\bförder-?tennis\b/i,
  /\b(erfolgreich(er|es|e)?|spannend(er|es|e)?)\s+tennis/i,
  /\btennis(nachwuchs|jugend)\b.*\b(artikel|news|bericht|meldung)\b/i,
  /\b(artikel|news|bericht|meldung)\b.*\btennis(nachwuchs|jugend)\b/i,
  /-\s*[\w.-]+\.(de|com|net|org)\s*$/i,
  /\-\s*(main-?post|deutschlandfunk(?:\s*kultur)?|main-?echo|merkur|pnp|kn-online|süddeutsche|faz|zeit|spiegel|focus|bild|tagesspiegel|n-tv|sport1|ndr|wdr|swr|mdr|br)\s*$/i,
  /\b(mannschaftsmeister|bayerischer\s+(meister|mannschaftsmeister))\b/i,
  /\btennissport\s+im\s+alter\b/i,
  /\beine\s+liga\s+zu\s+gut\b/i,
]);

function __asStr(v) {
  return String(v ?? "").trim();
}

/** @type {Set<string>} */
let __dynamicBlockedHosts = new Set();

/**
 * @param {string[]|Set<string>|null|undefined} hosts
 */
export function setClubWebsiteBlocklistHosts(hosts) {
  const next = new Set();
  for (const host of hosts instanceof Set ? hosts : Array.isArray(hosts) ? hosts : []) {
    const normalized = __asStr(host).toLowerCase().replace(/^www\./, "");
    if (normalized) next.add(normalized);
  }
  __dynamicBlockedHosts = next;
}

/**
 * @returns {string[]}
 */
export function getClubWebsiteBlocklistHosts() {
  return [...__dynamicBlockedHosts];
}

/**
 * @param {string} hostname
 */
export function isDynamicClubWebsiteBlocklistHost(hostname) {
  const host = __asStr(hostname).toLowerCase().replace(/^www\./, "");
  if (!host) return false;
  return __dynamicBlockedHosts.has(host);
}

/**
 * @param {string} hostname
 */
export function isClubWebsiteHostBlocked(hostname) {
  if (isDynamicClubWebsiteBlocklistHost(hostname)) return true;
  return isUntrustedClubWebsiteHost(hostname);
}

/**
 * @param {string} hostname
 */
export function isUntrustedClubWebsiteHost(hostname) {
  const host = __asStr(hostname).toLowerCase().replace(/^www\./, "");
  if (!host) return false;
  if (UNTRUSTED_CLUB_WEBSITE_HOSTS.has(host)) return true;
  for (const untrusted of UNTRUSTED_CLUB_WEBSITE_HOSTS) {
    if (host.endsWith(`.${untrusted}`)) return true;
  }
  return false;
}

/**
 * SERP-Titel / gespeicherter Vereinsname klingt nach Verzeichnis, Portal oder Listicle.
 *
 * @param {string} name
 */
export function isLikelyNonClubEntityName(name) {
  const text = __asStr(name);
  if (!text) return false;
  return NON_CLUB_ENTITY_NAME_PATTERNS.some((pattern) => pattern.test(text));
}

/**
 * @param {{ title?: string, snippet?: string, link?: string, url?: string }} item
 */
export function isLikelyNonClubDiscoverItem(item) {
  const title = __asStr(item?.title);
  const snippet = __asStr(item?.snippet);
  const combined = `${title} ${snippet}`;
  if (isLikelyNonClubEntityName(combined)) return true;

  if (/\b(tennisschule|tennis-?schule|tennisacademy|tennis-?academy|förder-?tennis)\b/i.test(combined)) {
    return true;
  }

  if (/\b(erfolgreich(er|es|e)?|besiegt|gewinnt|verliert|titel|meister)\b/i.test(title)) {
    if (!/\b(tennisclub|tennisverein|tc |tv |\be\.?\s*v\.?\b)/i.test(title)) {
      return true;
    }
  }

  if (/\b(verzeichnis|liste|news)\b/i.test(title) && !/\b(tennisclub|tennisverein|tc |tv )\b/i.test(title)) {
    return true;
  }

  if (/^[^|]*\bübersicht\b[^|]*$/i.test(title) && !/\b(tennisclub|tennisverein|\be\.?\s*v\.?\b)/i.test(title)) {
    return true;
  }

  return false;
}
